import React, { useCallback, useEffect, useState } from 'react';
import StateEnum from '@constants/state.enum';
import useNotification from '@hooks/notification/useNotification';
import ClientInfoModel from '@models/clientInfo';
import { getClientInfo, getGeneralForm } from '@services/form/form-http.service';
import { GetServerSideProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import { Spin } from 'antd';
import Script from 'next/script';
import getConfig from 'next/config';
import FormBuilder from '@components/client-components/form-builder/FormBuilder';
import { FormModel } from '@models/form';
import { useTranslation } from 'next-i18next';
import styles from './GeneralCv.module.scss';

const {
  publicRuntimeConfig: { GOOGLE_RECAPTCHA_KEY },
} = getConfig();

const GeneralCvPage: NextPage = () => {
  const router = useRouter();
  const { i18n } = useTranslation('common');
  const slugClient = router.query.slug ? (router.query.slug as string) : '';
  const [loading, setLoading] = useState<boolean>(true);
  const [clientInfo, setClientInfo] = useState<ClientInfoModel>();
  const [formFields, setFormFields] = useState<FormModel>([]);

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );
  const [companyNotFoundError] = useNotification(
    StateEnum.ERROR,
    'Company not found',
    'Oops, the company you are trying does not exist.',
  );

  const fetchClientInfo = useCallback(async () => {
    if (slugClient) {
      try {
        const client: ClientInfoModel = await getClientInfo(slugClient);
        setClientInfo(client);
        window.document.body.scrollTo({
          top: 7000,
          behavior: 'smooth',
        });
      } catch (e: any) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        e.ErrorMessage === 'company name not found' && companyNotFoundError();
        setLoading(true);
        generalError();
      }
    }
  }, [slugClient]);

  const fetchForm = useCallback(async () => {
    try {
      const formFieldsElem: FormModel = await getGeneralForm(slugClient, i18n.language);
      setFormFields(formFieldsElem);
      setLoading(false);
    } catch (e: any) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      e.ErrorMessage === 'company name not found' ? companyNotFoundError() : generalError();
      setLoading(true);
    }
  }, [slugClient]);

  useEffect(() => {
    setLoading(true);
    fetchClientInfo();
    fetchForm();
  }, [fetchClientInfo, fetchForm]);

  return (
    <>
      <Script src={`https://www.google.com/recaptcha/api.js?render=${GOOGLE_RECAPTCHA_KEY}`} />
      <section className={styles.compWrap}>
        {!clientInfo ? (
          <Spin size="large" />
        ) : (
          <div className={styles.secondStepWrap} style={{ display: 'flex' }}>
            <div className={styles.titleIconWrap}>
              <h3 className={styles.title}>Αποστολή Βιογραφικού</h3>
            </div>
            {loading ? (
              <Spin size="large" />
            ) : (
              <FormBuilder formFields={formFields} slug={slugClient} lang={i18n.language} clientColor="#ff2458" />
            )}
          </div>
        )}
      </section>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    return {
      props: {
        ...(await serverSideTranslations(context.locale!, ['common'])),
      },
    };
  } catch (err: any) {
    return {
      props: {
        errorCode: err.status || err?.response?.status || 500,
        ...(await serverSideTranslations(context.locale!, ['common'])),
      },
    };
  }
};

export default GeneralCvPage;
